/**
 * Payroll Tab Component for HR Payroll Page
 * Displays payroll records with add/delete functionality
 */
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DollarSign, Plus, Trash2 } from "lucide-react";

export function PayrollTab({
  payrollRecords,
  loading,
  onAddClick,
  onDelete,
  getEmployeeName,
  calculateGrossPay,
  calculateNetPay
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="payroll-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Payroll Records</h2>
          <p className="text-sm text-slate-500">Manage employee pay periods and compensation</p>
        </div>
        <Button onClick={onAddClick} className="gap-2" data-testid="add-payroll-btn">
          <Plus className="w-4 h-4" />
          Add Payroll Record
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="spinner w-8 h-8 mx-auto"></div>
        </div>
      ) : payrollRecords.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No payroll records yet</p>
          <p className="text-sm">Click &quot;Add Payroll Record&quot; to create one</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Pay Period</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Gross Pay</TableHead>
                <TableHead>Deductions</TableHead>
                <TableHead>Net Pay</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payrollRecords.map((record) => (
                <TableRow key={record.id}>
                  <TableCell className="font-medium">{getEmployeeName(record.employee_id)}</TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {record.pay_period_start} - {record.pay_period_end}
                    </span>
                  </TableCell>
                  <TableCell>{record.hours_worked}h</TableCell>
                  <TableCell>${record.hourly_rate}/hr</TableCell>
                  <TableCell className="font-medium">${calculateGrossPay(record).toFixed(2)}</TableCell>
                  <TableCell className="text-red-600">-${record.deductions?.toFixed(2) || "0.00"}</TableCell>
                  <TableCell className="font-bold text-emerald-600">${calculateNetPay(record).toFixed(2)}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      record.status === "paid" 
                        ? "bg-emerald-100 text-emerald-700"
                        : record.status === "pending"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-slate-100 text-slate-700"
                    }`}>
                      {record.status || "Draft"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-red-500"
                      onClick={() => onDelete(record.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
